import { getUserHistories } from '@/services/api'
import type { HistoryEntry, HistoryOrder, WalletHistory } from '@/types/history'
import { formatPrimaryAmount, formatTimeLabel } from '@/utils/format'
import { useInfiniteQuery } from '@tanstack/react-query'

// 型定義
export interface DatabaseHistoryEntry {
  id: string
  type: 'receive' | 'send'
  title: string
  primaryAmount: string
  secondaryAmount?: string
  timeLabel: string
  createdAt: Date
  amount: number
  asset: {
    id: string
    name: string
    symbol: string
    decimals: number
  }
  onchainHistory?: {
    id: string
    txHash: string
    traceId?: string | null
    fromAddress: string
    toAddress: string
    value: string
    blockNumber: number
    blockTimestamp: string
    createdAt: string
  } | null
  order?: HistoryOrder | null
}

const toHistoryEntry = (history: WalletHistory): HistoryEntry => {
  const createdAt = new Date(history.createdAt)
  const amount = history.amount
  const type = amount >= 0 ? 'receive' : 'send'

  return {
    id: history.id,
    type,
    title: history.category?.name ?? '取引',
    primaryAmount: formatPrimaryAmount(amount, history.asset?.symbol ?? ''),
    secondaryAmount: history.description ?? undefined,
    timeLabel: formatTimeLabel(createdAt),
    createdAt,
    amount,
  }
}

const toDatabaseHistoryEntry = (history: WalletHistory): DatabaseHistoryEntry => {
  return {
    id: history.id,
    type: toHistoryEntry(history).type,
    title: toHistoryEntry(history).title,
    primaryAmount: toHistoryEntry(history).primaryAmount,
    secondaryAmount: toHistoryEntry(history).secondaryAmount,
    timeLabel: toHistoryEntry(history).timeLabel,
    createdAt: toHistoryEntry(history).createdAt,
    amount: toHistoryEntry(history).amount,
    asset: history.asset,
    onchainHistory: history.onchainHistory,
    order: history.order,
  }
}

const fetchDatabaseHistories = async ({
  pageParam = 0,
  pageSize = 50,
}: {
  pageParam?: number
  pageSize?: number
}): Promise<{
  events: DatabaseHistoryEntry[]
  nextCursor?: number
  hasMore: boolean
}> => {
  const response = await getUserHistories({
    limit: pageSize,
    offset: pageParam,
    type: 'all',
  })

  const histories = response.histories ?? []
  const events = histories.map(toDatabaseHistoryEntry)

  // 時系列順にソート（新しい順）
  events.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())

  const hasMore = histories.length === pageSize

  return {
    events,
    nextCursor: hasMore ? pageParam + pageSize : undefined,
    hasMore,
  }
}

export const useDatabaseHistories = () => {
  return useInfiniteQuery({
    queryKey: ['histories', 'database'],
    queryFn: ({ pageParam = 0 }) =>
      fetchDatabaseHistories({
        pageParam,
        pageSize: 50,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: Number.POSITIVE_INFINITY, // Appendオンリーなので既存データは更新されない
    gcTime: Number.POSITIVE_INFINITY, // データを永続的に保持
    refetchInterval: 5 * 60 * 1000, // 5分ごとに新しいデータをチェック
  })
}
